import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Button,
  Divider,
  Dropdown,
  Menu,
  Modal,
  Segmented,
  Tooltip,
  message
} from "antd";
import "../submittal-list.css";
import "./filter-bar.scss";
import { FilterChips } from "components";
import { ActionBar } from "components/navbar";
import SearchInput from "components/search-input";
import { FilterItem } from "models/types";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  MUTATION_UPDATE_AUDIT_EXPORTED_LOGS,
  MUTATION_UPDATE_SUBMITTALS
} from "services/graphQL/mutations";
import { IntegrationProjectInfo } from "components/integration-project-info";
import ErrorBoundary from "components/error-boundary";
import ColumnConfigViews, {
  TSaveColumnState
} from "components/column-config-views";
import { CloseOutlined, DownOutlined } from "@ant-design/icons";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import {
  DATE_FORMAT_MMDDYYYY_HHMM_A_Z,
  INPUT_DATE_FORMAT
} from "utils/dateutils";
import moment from "moment";
import { useHistory } from "react-router";
import { CiqIconBox } from "components/svg-icons";
import { AgGridReact } from "ag-grid-react";
import {
  AuditLogFeatureType,
  AuditLogFeatures,
  EFeaturePageId,
  ErrorMessages,
  ProjectPermissionEnum,
  SuccessMessages
} from "../../../constants";

export type FilterProps = {
  gridRef: React.RefObject<AgGridReact<any>>;
  items: FilterItem[];
  onCreateLogClick: Function;
  onImportLogClick: Function;
  onAllSubmittalsClick: Function;
  showFiterChips: boolean;
  onSearch: any;
  stats: any;
  onExportCSV: Function;
  selectedRow: Array<any>;
  onSubmittalEditClick: (
    panel: "details" | "duration" | "deadline_date" | "extract_material"
  ) => void;
  isIntegrationMode: boolean;
  displayRowCount: number;
  isIntegrationConfigured: boolean;
  resetFilters: Function;
  setFilterStateFromData: (columnState: TSaveColumnState) => void;
  afterApplyFilterCallBack?: Function;
  getColumnStateFromGrid: () => {
    columnList: any;
    quickSearch: string;
  } | null;
  isGridDataRendered: boolean;
  subCounts?: any | null;
  segmentAction?: Function;
  showDeletedTxt?: boolean;
  handleDeleteSubmittal?: (submittalId: string[]) => void;
};

function SubmittalListFilterComponent(props: FilterProps) {
  const {
    gridRef,
    items,
    onCreateLogClick,
    onImportLogClick,
    onAllSubmittalsClick,
    showFiterChips,
    onSearch,
    stats,
    onExportCSV,
    selectedRow,
    onSubmittalEditClick,
    isIntegrationMode,
    displayRowCount,
    isIntegrationConfigured,
    resetFilters,
    setFilterStateFromData,
    getColumnStateFromGrid,
    afterApplyFilterCallBack,
    isGridDataRendered,
    subCounts: submittalsCount,
    segmentAction,
    showDeletedTxt,
    handleDeleteSubmittal
  } = props;

  const selected = selectedRow ? selectedRow.length : 0;
  const history = useHistory();
  const {
    tokenContents,
    gqlClientForProject,
    projectIntegrationConfig,
    lastImportLogData,
    projectDetails,
    isFeatureFlagEnabled,
    isProcoreProject,
    columnHeaders: { submittalHeaderMap }
  } = useContext(ProjectContext);

  const isBulkLinkingEnabled = isFeatureFlagEnabled(
    "MATERIAL_MULTIPLE_LINKING"
  );

  const isGroupMaterialCreationEnabled = isFeatureFlagEnabled(
    "GROUPED_MATERIAL_CREATION"
  );

  const [updateSubmittals] = useCIQMutation(MUTATION_UPDATE_SUBMITTALS, {
    client: gqlClientForProject
  });

  const [saveExportAuditLogEvent] = useCIQMutation(
    MUTATION_UPDATE_AUDIT_EXPORTED_LOGS,
    {
      client: gqlClientForProject
    }
  );

  const lastImportStatus = lastImportLogData?.status;

  const onChipDelete = (item: { field: string; value: string }) => {
    const filterModel = gridRef.current?.api.getFilterModel();
    const model = filterModel ? filterModel[item.field] : null;
    if (model) {
      const values = model.values.filter((val: any) => val !== item.value);
      if (values.length === 0) {
        gridRef.current?.api.destroyFilter(item.field);
        return;
      }
      gridRef.current?.api?.setColumnFilterModel(item.field, {
        ...model,
        values
      });
      gridRef.current?.api.onFilterChanged();
    }
  };

  const onCategoryDelete = (item: FilterItem) => {
    gridRef.current?.api.destroyFilter(item.field);
  };

  const cannotCreateSubmittal = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateSubmittal,
    tokenContents?.role!
  );

  const deleteSubmittals = useCallback(() => {
    Modal.confirm({
      title: "Are you sure you want to delete the selected submittal(s)?",
      okText: "Yes",
      cancelText: "No",
      async onOk() {
        if (handleDeleteSubmittal) {
          handleDeleteSubmittal(selectedRow.map((s) => s.id));
          return;
        }
        const deletedSubmittalsRes = await updateSubmittals({
          variables: {
            set: { deleted: true },
            where: {
              id: { _in: selectedRow.map((s) => s.id) }
            }
          }
        });
        if (deletedSubmittalsRes.data) {
          message.success(SuccessMessages.deleteSuccess);
        }
        if (deletedSubmittalsRes.errors) {
          console.error(deletedSubmittalsRes.errors);
          message.error("Failed to delete Submittals.");
        }
      }
    });
  }, [handleDeleteSubmittal, selectedRow, updateSubmittals]);

  const momentUpdateIntervalRef = useRef<any>(null);
  const [momentInstance, setMomentInstance] = useState<any>(null);

  const importEnableTime = useMemo(() => {
    if (!projectIntegrationConfig?.length) return null;
    const systemIntgConfig =
      projectIntegrationConfig?.length &&
      projectIntegrationConfig[0]?.systemIntgConfigProperties;
    return systemIntgConfig?.SYNC_INTERVAL;
  }, [projectIntegrationConfig]);

  const nextAllowedImportTime = moment(
    lastImportLogData?.lastSyncDateTime,
    INPUT_DATE_FORMAT
  ).add(importEnableTime, "minutes");

  const canImportNewFile = useMemo(() => {
    if (!lastImportLogData) return true;
    return momentInstance?.isSameOrAfter(nextAllowedImportTime);
  }, [momentInstance, nextAllowedImportTime, lastImportLogData]);

  useEffect(() => {
    momentUpdateIntervalRef.current = setInterval(() => {
      if (moment().isUtcOffset()) {
        setMomentInstance(moment());
        if (canImportNewFile) {
          clearInterval(momentUpdateIntervalRef.current);
          momentUpdateIntervalRef.current = null;
        }
      }
    }, 1000);
  }, [canImportNewFile]);

  const syncBtnVisible = useMemo(() => {
    if (!projectIntegrationConfig?.length) return false;
    const isSyncBtnVisible =
      projectIntegrationConfig &&
      projectIntegrationConfig.length > 0 &&
      projectIntegrationConfig[0]?.systemIntgConfigProperties
        ?.INTEGRATION_METHOD === "api";
    return isSyncBtnVisible;
  }, [projectIntegrationConfig]);

  const isFileBasedIntegration = useMemo(() => {
    const systemIntgConfig =
      projectIntegrationConfig &&
      projectIntegrationConfig.length > 0 &&
      projectIntegrationConfig[0]?.systemIntgConfigProperties;

    return isIntegrationMode && systemIntgConfig?.INTEGRATION_METHOD === "file";
  }, [isIntegrationMode, projectIntegrationConfig]);

  const disabledMessage = useMemo(() => {
    if (isIntegrationMode && syncBtnVisible)
      return ErrorMessages.ActionDisabledDueToIntegrationMode;

    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;

    return "";
  }, [cannotCreateSubmittal, isIntegrationMode, syncBtnVisible]);

  const hoverMsgForFileBasedImportBtn = useMemo(() => {
    if (isFileBasedIntegration) {
      if (lastImportStatus && lastImportStatus === "PROCESSING") {
        return "Import is in Progress.";
      }

      if (!canImportNewFile) {
        return `You can only import again after ${nextAllowedImportTime.format(
          DATE_FORMAT_MMDDYYYY_HHMM_A_Z
        )}`;
      }
      return "Import Log";
    }
    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;
    return "";
  }, [
    isFileBasedIntegration,
    cannotCreateSubmittal,
    lastImportStatus,
    canImportNewFile,
    nextAllowedImportTime
  ]);

  const bulkEditActionMenuItems = useMemo(() => {
    return (
      <Menu>
        <Menu.ItemGroup key="bulk-edit" title="Bulk Edit">
          {!isIntegrationMode && (
            <Menu.Item
              onClick={() => {
                onSubmittalEditClick("details");
              }}
              key={0}
            >
              Details
            </Menu.Item>
          )}
          <Menu.Item
            key="deadline_date"
            onClick={() => onSubmittalEditClick("deadline_date")}
          >
            {submittalHeaderMap?.manual_entry_date || "Deadline Date"}
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              onSubmittalEditClick("duration");
            }}
            key={1}
            data-testid="bulk-edit-durations-button"
          >
            Durations
          </Menu.Item>
          {isBulkLinkingEnabled && (
            <Menu.Item
              key="link"
              onClick={() => {
                history.push(
                  `/project/${projectDetails?.id}/schedule/submittal-linking`,
                  {
                    name: "Bulk Edit Linking",
                    selectedRows: selectedRow
                  }
                );
              }}
            >
              Links
            </Menu.Item>
          )}
        </Menu.ItemGroup>

        {/* Divider */}
        <Menu.Divider />

        {isGroupMaterialCreationEnabled && (
          <Menu.Item
            onClick={() => {
              onSubmittalEditClick("extract_material");
            }}
            key="extract_material"
            data-testid="bulk-extract-materials-button"
          >
            Create Materials
          </Menu.Item>
        )}
      </Menu>
    );
  }, [
    history,
    isBulkLinkingEnabled,
    isIntegrationMode,
    isGroupMaterialCreationEnabled,
    onSubmittalEditClick,
    projectDetails?.id,
    selectedRow,
    submittalHeaderMap?.manual_entry_date
  ]);

  const bulkEditButton = useMemo(() => {
    if (selected < 1) return null;

    return (
      <div className="bg-ciq-gray-500 bg-opacity-70 py-1 px-4 flex items-center space-x-2 rounded-md">
        <div className="py-1">{selected} Selected</div>
        {isGroupMaterialCreationEnabled && selected === 1 && (
          <Button
            onClick={() => {
              onSubmittalEditClick("extract_material");
            }}
          >
            Create Materials
          </Button>
        )}
        {selected > 1 && (
          <Dropdown
            overlay={bulkEditActionMenuItems}
            disabled={cannotCreateSubmittal}
          >
            <Button
              disabled={cannotCreateSubmittal}
              title={
                cannotCreateSubmittal
                  ? ErrorMessages.PermissionNotGranted
                  : "Actions"
              }
              data-testid="bulk-edit-button"
            >
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        )}
        {!isIntegrationMode && (
          <Button
            onClick={deleteSubmittals}
            size="middle"
            className="bottomBarBtn"
            title={disabledMessage || "Delete"}
            disabled={isIntegrationMode || cannotCreateSubmittal}
            data-testid="delete-button"
          >
            Delete
          </Button>
        )}
      </div>
    );
  }, [
    bulkEditActionMenuItems,
    cannotCreateSubmittal,
    deleteSubmittals,
    disabledMessage,
    isIntegrationMode,
    isGroupMaterialCreationEnabled,
    onSubmittalEditClick,
    selected
  ]);

  const saveExportAuditLog = async () => {
    const payload: any = {
      variables: {
        eventType: AuditLogFeatureType.LOG_EXPORTED,
        feature: AuditLogFeatures.SUBMITTAL_LOG
      }
    };
    await saveExportAuditLogEvent(payload);
  };

  const disableAccImportLogBtn = useMemo(() => {
    return (
      cannotCreateSubmittal ||
      (lastImportStatus && lastImportStatus === "PROCESSING") ||
      !canImportNewFile
    );
  }, [canImportNewFile, cannotCreateSubmittal, lastImportStatus]);

  return (
    <ActionBar>
      <div className="flex shrink-0 items-center space-x-3">
        <CiqIconBox feature="SUBMITTALS" size={20} padding={8} />
        <div className="flex flex-col">
          <div className="flex space-x-2 text-black">
            <div className="text-xl font-semibold">Submittals</div>
            {(() => {
              if (!stats || stats === "") {
                return null;
              }
              if (stats.props) {
                return (
                  <>
                    <div
                      className="flex flex-wrap items-center border border-solid border-ciq-gray-100 rounded-xl px-2 text-black text-opacity-50 text-xs font-normal"
                      data-testid="log-items-count"
                    >
                      {stats.props.children[0]}
                    </div>
                    <Tooltip title="Remove to view all Submittals">
                      <div className="flex flex-wrap items-center border border-solid rounded-xl px-1 text-xs font-normal bg-ciq-blue-100 text-ciq-blue-700 border-ciq-blue-300">
                        {stats.props.children[1]}
                        <CloseOutlined
                          className="mt-[3px] text-xs text-ciq-blue-600"
                          style={{ fontSize: 10 }}
                          onClick={() => {
                            onAllSubmittalsClick();
                          }}
                          data-testid="button-clear-filter"
                        />
                      </div>
                    </Tooltip>
                  </>
                );
              }
              return (
                <div
                  className="flex flex-wrap items-center border border-solid border-ciq-gray-100 rounded-xl px-2 text-black text-opacity-50 text-xs font-normal"
                  data-testid="log-items-count"
                >
                  {stats}
                </div>
              );
            })()}
          </div>
          <div className="flex h-6 items-center">
            {showDeletedTxt && (
              <div className="text-ciq-gray-800 font-medium text-sm opacity-80">
                (Deleted)
              </div>
            )}
            {isIntegrationMode && isIntegrationConfigured && (
              <ErrorBoundary>
                <IntegrationProjectInfo
                  syncBtnVisible={syncBtnVisible}
                  isFileBasedIntegration={isFileBasedIntegration}
                />
              </ErrorBoundary>
            )}
          </div>
        </div>
      </div>

      <div className="flex space-x-2 items-center">
        {bulkEditButton}
        {showFiterChips && (
          <FilterChips
            items={items}
            onChipDelete={onChipDelete}
            onCategoryDelete={onCategoryDelete}
            resetAll={resetFilters}
          />
        )}
        <div className="flex">
          <SearchInput
            placeholder="Search Submittals"
            onChange={onSearch}
            disabled={isGridDataRendered}
            data-testid="input-search-submittals"
          />
        </div>
        <ColumnConfigViews
          setFilterStateFromData={setFilterStateFromData}
          getColumnStateFromGrid={getColumnStateFromGrid}
          afterApplyFilterCallBack={afterApplyFilterCallBack}
          featureId={EFeaturePageId.SUBMITTAL}
        />

        {isIntegrationMode && isFileBasedIntegration && (
          <Tooltip
            title={hoverMsgForFileBasedImportBtn}
            overlayInnerStyle={{
              backgroundColor: "white",
              opacity: 0.88
            }}
          >
            <Button
              disabled={disableAccImportLogBtn}
              onClick={() => onImportLogClick()}
              data-testid="integration-import-log-button"
            >
              Import Log
            </Button>
          </Tooltip>
        )}

        {!isIntegrationMode && (
          <Button
            title={disabledMessage || "Import Log"}
            disabled={isIntegrationMode || cannotCreateSubmittal}
            onClick={() => onImportLogClick()}
            data-testid="import-log-button"
          >
            Import Log
          </Button>
        )}

        <Button
          size="middle"
          title="Export Submittal Log"
          disabled={!displayRowCount}
          onClick={() => {
            onExportCSV();
            saveExportAuditLog();
          }}
          data-testid="export-button"
        >
          Export
        </Button>

        {!isIntegrationMode && (
          <Button
            type="primary"
            title={disabledMessage || "New Submittal"}
            disabled={isIntegrationMode || cannotCreateSubmittal}
            onClick={() => {
              onCreateLogClick();
            }}
            data-testid="new-submittal-button"
          >
            New Submittal
          </Button>
        )}
        {isProcoreProject && (
          <div className="flex space-x-2 filter-bar">
            <Divider
              type="vertical"
              className="h-[28px] mt-[2px] bg-black bg-opacity-20"
            />
            <Segmented
              defaultChecked
              className="bg-transparent border-solid border border-ciq-border-light p-[1px] rounded checked:font-extrabold"
              options={[
                {
                  label: (
                    <div style={{ padding: 0 }}>
                      <div>All</div>
                    </div>
                  ),
                  value: "true"
                },
                {
                  label: (
                    <div style={{ padding: 0 }}>
                      <div>
                        Recycle Bin{" "}
                        {`(${submittalsCount?.deletedSubCount || 0})`}
                      </div>
                    </div>
                  ),
                  value: "false"
                }
              ]}
              onChange={(value) => {
                if (segmentAction) {
                  segmentAction(value);
                }
              }}
            />
          </div>
        )}
      </div>
    </ActionBar>
  );
}

export default SubmittalListFilterComponent;
